import { canonicalBands } from './criteriaDiff';
import { formatMinimum, measureName } from './criteriaView';
import type { GradeBand, Outcome } from './settingsApi';

export type DraftCondition = { measure: string; value: string };
export type DraftRule = { outcome: Outcome; conditions: DraftCondition[]; displayNote?: string };
export type DraftBand = { assessment: GradeBand['assessment']; grades: 'all' | string[]; noExit: boolean; rules: DraftRule[] };

export const BLANK_VALUE_ERROR = 'Enter a value';
export const ACCESS_RANGE_ERROR = 'Enter a value from 1.0 to 6.0';
export const ALTERNATE_RANGE_ERROR = 'Enter a whole number from 1 to 5';
export const DUPLICATE_MEASURE_ERROR = 'Already in this rule';
export const LITERACY_ABOVE_OVERALL_ERROR = 'Can’t be above the Overall composite minimum';
export const ALTERNATE_LITERACY_ABOVE_OVERALL_ERROR = 'Can’t be above the Overall minimum';
export const REVIEW_ABOVE_EXIT_ERROR = 'Can’t be above the Exits minimum';

const MEASURES = ['overall', 'literacy', 'oral', 'comprehension', 'listening', 'speaking', 'reading', 'writing'];

export function isAlternate(band: { assessment: GradeBand['assessment'] }): boolean {
  return band.assessment === 'alternate_access';
}

export function measureChoices(alternate: boolean): { value: string; label: string }[] {
  return MEASURES.map((value) => ({ value, label: measureName(value, alternate) }));
}

export function draftFromBands(bands: GradeBand[]): DraftBand[] {
  return bands.map((band) => ({
    assessment: band.assessment,
    grades: band.grades,
    noExit: band.noExit,
    rules: band.ruleSets.map((rule) => ({
      outcome: rule.outcome,
      conditions: rule.conditions.map((c) => ({ measure: c.measure, value: formatMinimum(c.minimum, isAlternate(band)) })),
      ...(rule.displayNote ? { displayNote: rule.displayNote } : {}),
    })),
  }));
}

export function bandsFromDraft(draft: DraftBand[]): GradeBand[] {
  return draft.map((band) => ({
    assessment: band.assessment,
    grades: band.grades,
    noExit: band.noExit,
    ruleSets: band.rules
      .filter((rule) => rule.conditions.length > 0)
      .map((rule) => ({
        outcome: rule.outcome,
        conditions: rule.conditions.map((c) => ({ measure: c.measure, minimum: Number(c.value) })),
        ...(rule.displayNote ? { displayNote: rule.displayNote } : {}),
      })),
  }));
}

export function conditionError(value: string, alternate: boolean): string | null {
  const text = value.trim();
  if (text === '') return BLANK_VALUE_ERROR;
  const n = Number(text);
  if (alternate) return /^\d$/.test(text) && n >= 1 && n <= 5 ? null : ALTERNATE_RANGE_ERROR;
  return /^\d(\.\d)?$/.test(text) && n >= 1 && n <= 6 ? null : ACCESS_RANGE_ERROR;
}

export function conditionErrors(draft: DraftBand[], bandIndex: number, ruleIndex: number): (string | null)[] {
  const band = draft[bandIndex];
  const rule = band?.rules[ruleIndex];
  if (!band || !rule) return [];
  const alternate = isAlternate(band);
  const overallMinimum = validOverall(rule, alternate);
  const exitMinimum = rule.outcome === 'eligible_for_review' ? validOverall(band.rules.find((r) => r.outcome === 'exits'), alternate) : null;
  const seen = new Set<string>();
  return rule.conditions.map((c) => {
    const error = conditionError(c.value, alternate);
    const duplicate = seen.has(c.measure);
    seen.add(c.measure);
    if (error) return error;
    if (duplicate) return DUPLICATE_MEASURE_ERROR;
    if (c.measure === 'literacy' && overallMinimum !== null && Number(c.value) > overallMinimum) {
      return alternate ? ALTERNATE_LITERACY_ABOVE_OVERALL_ERROR : LITERACY_ABOVE_OVERALL_ERROR;
    }
    if (c.measure === 'overall' && exitMinimum !== null && Number(c.value) > exitMinimum) return REVIEW_ABOVE_EXIT_ERROR;
    return null;
  });
}

function validOverall(rule: DraftRule | undefined, alternate: boolean): number | null {
  const overall = rule?.conditions.find((c) => c.measure === 'overall');
  return overall && conditionError(overall.value, alternate) === null ? Number(overall.value) : null;
}

export function draftHasErrors(draft: DraftBand[]): boolean {
  return draft.some((band, b) => band.rules.some((_, r) => conditionErrors(draft, b, r).some((e) => e !== null)));
}

export function canAddCondition(draft: DraftBand[], bandIndex: number, ruleIndex: number): boolean {
  const band = draft[bandIndex];
  const rule = band?.rules[ruleIndex];
  if (!band || !rule || band.noExit) return false;
  const used = new Set(rule.conditions.map((c) => c.measure));
  return measureChoices(isAlternate(band)).some((m) => !used.has(m.value));
}

export function canRemoveCondition(draft: DraftBand[], bandIndex: number, ruleIndex: number, conditionIndex: number): boolean {
  const band = draft[bandIndex];
  const rule = band?.rules[ruleIndex];
  if (!band || !rule || band.noExit || conditionIndex >= rule.conditions.length) return false;
  return rule.conditions.length > 1 || band.rules.length > 1;
}

export function draftDirty(draft: DraftBand[], saved: GradeBand[]): boolean {
  return draftHasErrors(draft) || canonicalBands(bandsFromDraft(draft)) !== canonicalBands(saved);
}

function updateRule(draft: DraftBand[], bandIndex: number, ruleIndex: number, change: (rule: DraftRule, band: DraftBand) => DraftRule | null): DraftBand[] {
  return draft.map((band, b) => {
    if (b !== bandIndex) return band;
    const rules = band.rules.flatMap((rule, r) => {
      if (r !== ruleIndex) return [rule];
      const next = change(rule, band);
      return next === null ? [] : [next];
    });
    return { ...band, rules };
  });
}

function updateCondition(draft: DraftBand[], bandIndex: number, ruleIndex: number, conditionIndex: number, change: (c: DraftCondition) => DraftCondition): DraftBand[] {
  return updateRule(draft, bandIndex, ruleIndex, (rule) => ({
    ...rule,
    conditions: rule.conditions.map((c, i) => (i === conditionIndex ? change(c) : c)),
  }));
}

export function setConditionValue(draft: DraftBand[], bandIndex: number, ruleIndex: number, conditionIndex: number, value: string): DraftBand[] {
  return updateCondition(draft, bandIndex, ruleIndex, conditionIndex, (c) => ({ ...c, value }));
}

export function setConditionMeasure(draft: DraftBand[], bandIndex: number, ruleIndex: number, conditionIndex: number, measure: string): DraftBand[] {
  return updateCondition(draft, bandIndex, ruleIndex, conditionIndex, (c) => ({ ...c, measure }));
}

export function addCondition(draft: DraftBand[], bandIndex: number, ruleIndex: number): DraftBand[] {
  if (!canAddCondition(draft, bandIndex, ruleIndex)) return draft;
  return updateRule(draft, bandIndex, ruleIndex, (rule, band) => {
    const used = new Set(rule.conditions.map((c) => c.measure));
    const alternate = isAlternate(band);
    const measure = measureChoices(alternate).find((m) => !used.has(m.value))?.value ?? 'overall';
    return { ...rule, conditions: [...rule.conditions, { measure, value: alternate ? '4' : '4.5' }] };
  });
}

export function removeCondition(draft: DraftBand[], bandIndex: number, ruleIndex: number, conditionIndex: number): DraftBand[] {
  if (!canRemoveCondition(draft, bandIndex, ruleIndex, conditionIndex)) return draft;
  return updateRule(draft, bandIndex, ruleIndex, (rule) => {
    const conditions = rule.conditions.filter((_, i) => i !== conditionIndex);
    return conditions.length === 0 ? null : { ...rule, conditions };
  });
}

export function addReviewRule(draft: DraftBand[], bandIndex: number): DraftBand[] {
  return draft.map((band, b) => {
    if (b !== bandIndex) return band;
    const value = isAlternate(band) ? '4' : '4.0';
    return { ...band, rules: [...band.rules, { outcome: 'eligible_for_review', conditions: [{ measure: 'overall', value }] }] };
  });
}
